import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import getCookie from "../Utils/getCookie";

/**
 * Socket.io connects to the *origin* (host + port), not the API path.
 * Strip any trailing slash and path segments from REACT_APP_BASE_URL.
 * e.g. "http://localhost:6001/api" → "http://localhost:6001"
 */
const deriveSocketBase = (apiBase) => {
  try {
    const { protocol, host } = new URL(apiBase);
    return `${protocol}//${host}`;
  } catch {
    return (apiBase || "http://localhost:6001").replace(/\/+$/, "");
  }
};

const SOCKET_BASE = deriveSocketBase(
  process.env.REACT_APP_BASE_URL || "http://localhost:6001"
);

/**
 * @param {object}      handlers
 * @param {number|null} conversationId  – when provided the room is joined
 *                                        automatically on (re-)connect
 * @param {boolean}     joinAdminInbox  – join admin:{subscriptionId} for dashboard
 */
const useChatSocket = (
  handlers = {},
  conversationId = null,
  joinAdminInbox = false
) => {
  const socketRef = useRef(null);
  const handlersRef = useRef(handlers);
  const conversationIdRef = useRef(conversationId);
  const joinAdminInboxRef = useRef(joinAdminInbox);
  const joinedConversationIdRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  const leaveJoinedConversation = useCallback((socket = socketRef.current) => {
    const joinedId = joinedConversationIdRef.current;
    if (!joinedId || !socket?.connected) return;
    socket.emit("leave_conversation", { conversationId: joinedId });
    joinedConversationIdRef.current = null;
  }, []);

  const joinConversationRoom = useCallback(
    (socket, convId) => {
      if (!socket?.connected || !convId) return;

      const prevId = joinedConversationIdRef.current;
      if (prevId && prevId !== convId) {
        socket.emit("leave_conversation", { conversationId: prevId });
      }

      socket.emit("join_conversation", { conversationId: convId });
      joinedConversationIdRef.current = convId;
    },
    []
  );

  const joinRooms = useCallback(
    (socket) => {
      if (joinAdminInboxRef.current) {
        socket.emit("join_chat_admin");
      }
      if (conversationIdRef.current) {
        joinConversationRoom(socket, conversationIdRef.current);
      }
    },
    [joinConversationRoom]
  );

  useEffect(() => {
    conversationIdRef.current = conversationId;
    joinAdminInboxRef.current = joinAdminInbox;

    const socket = socketRef.current;
    if (!socket?.connected) return;

    if (conversationId) {
      joinConversationRoom(socket, conversationId);
      return;
    }

    leaveJoinedConversation(socket);
  }, [conversationId, joinAdminInbox, joinConversationRoom, leaveJoinedConversation]);

  useEffect(() => {
    const token = getCookie("token-siraj");

    const socket = io(`${SOCKET_BASE}/chat`, {
      auth: { token },
      transports: ["websocket"],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      joinRooms(socket);
    });

    socket.on("disconnect", () => {
      joinedConversationIdRef.current = null;
      setIsConnected(false);
    });

    socket.on("new_message", (data) =>
      handlersRef.current.onNewMessage?.(data)
    );
    socket.on("message_read", (data) =>
      handlersRef.current.onMessageRead?.(data)
    );
    socket.on("conversation_assigned", (data) =>
      handlersRef.current.onConversationAssigned?.(data)
    );
    socket.on("unread_count_update", (data) =>
      handlersRef.current.onUnreadUpdate?.(data)
    );
    socket.on("conversation_updated", (data) =>
      handlersRef.current.onConversationUpdated?.(data)
    );
    socket.on("error", (data) => handlersRef.current.onError?.(data));

    return () => {
      leaveJoinedConversation(socket);
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [joinRooms, leaveJoinedConversation]);

  const joinConversation = useCallback(
    (convId) => {
      conversationIdRef.current = convId;
      joinConversationRoom(socketRef.current, convId);
    },
    [joinConversationRoom]
  );

  const markRead = useCallback((convId) => {
    socketRef.current?.emit("mark_read", { conversationId: convId });
  }, []);

  return { isConnected, joinConversation, markRead };
};

export default useChatSocket;
